import { PaginationRequest } from 'app/business/common/paginationrequest';
import { extractMasterPage } from 'app/helper/masterpagehelper';
import { MasterService } from 'app/service/master.service';

/** Resolve a metadata section from direct or wrapped API payloads. */
export function getMetadataSectionList(metadata: any, candidateKeys: string[] = []): any[] {
  if (!metadata) {
    return [];
  }

  for (const key of candidateKeys) {
    if (Array.isArray(metadata[key])) {
      return metadata[key];
    }
  }

  for (const containerKey of ['metadata', 'Metadata', 'data', 'Data', 'result', 'Result', 'response', 'Response']) {
    const container = metadata[containerKey];
    if (!container || typeof container !== 'object') {
      continue;
    }
    for (const key of candidateKeys) {
      if (Array.isArray(container[key])) {
        return container[key];
      }
    }
  }

  return [];
}

export function normalizeSelectableOptions(items: any[]): any[] {
  return (items || [])
    .map((item: any) => {
      if (item === null || item === undefined) {
        return null;
      }
      if (typeof item === 'string' || typeof item === 'number') {
        const value = String(item).trim();
        return value ? { id: value, name: value, isActive: true } : null;
      }
      const id = item.id ?? item.Id ?? item.value ?? item.Value ?? item.key ?? item.Key ?? item.code ?? item.Code ?? item.name ?? item.Name ?? item.title ?? item.Title ?? item.label ?? item.Label;
      const name = item.name ?? item.Name ?? item.title ?? item.Title ?? item.label ?? item.Label ?? item.displayName ?? item.DisplayName ?? item.text ?? item.Text ?? item.description ?? item.Description ?? String(id ?? '').trim();
      if (!id && !name) {
        return null;
      }
      return {
        ...item,
        id: id ? String(id) : name,
        name: name || String(id),
        isActive: item.isActive !== false,
      };
    })
    .filter((item: any) => !!item?.id);
}

export function normalizeCategorySelection(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const id = record.id ?? record.Id ?? record.value ?? record.key;
    return id === null || id === undefined || id === '' ? null : String(id);
  }
  return String(value);
}

export function filterCachedChildCategories(
  cache: any[],
  parentId: string | number | null | undefined,
  parentField: 'categoryId' | 'subCategoryId'
): any[] {
  const normalizedParentId = normalizeCategorySelection(parentId);
  if (!normalizedParentId) {
    return [];
  }
  const parentFieldPascal =
    parentField === 'categoryId' ? 'CategoryId' : 'SubCategoryId';
  return (cache || []).filter((item) => {
    const itemParentId = String(
      item?.[parentField] ??
        item?.[parentFieldPascal] ??
        item?.parentId ??
        item?.ParentId ??
        ''
    );
    return itemParentId === normalizedParentId;
  });
}

export function mapMetadataCategories(options: any[]): any[] {
  return (options || [])
    .map((opt) => {
      const id = opt?.id ?? opt?.Id;
      const name = opt?.name ?? opt?.Name;
      if (id === null || id === undefined || id === '' || !name) {
        return null;
      }
      return {
        id: String(id),
        name: String(name),
        code: opt?.code ?? opt?.Code,
        description: opt?.description ?? opt?.Description ?? '',
        isActive: opt?.isActive !== false && opt?.IsActive !== false,
      };
    })
    .filter((item): item is NonNullable<typeof item> => !!item);
}

export function mapMetadataChildCategories(
  options: any[],
  parentField: 'categoryId' | 'subCategoryId'
): any[] {
  return (options || [])
    .map((opt) => {
      const id = opt?.id ?? opt?.Id;
      const name = opt?.name ?? opt?.Name;
      const parentId = String(
        opt?.parentId ?? opt?.ParentId ?? opt?.[parentField] ?? opt?.[parentField.charAt(0).toUpperCase() + parentField.slice(1)] ?? ''
      );
      if (id === null || id === undefined || id === '' || !name || !parentId) {
        return null;
      }
      return {
        id: String(id),
        name: String(name),
        code: opt?.code ?? opt?.Code,
        description: opt?.description ?? opt?.Description ?? '',
        isActive: opt?.isActive !== false && opt?.IsActive !== false,
        parentId,
        [parentField]: parentId,
      };
    })
    .filter((item): item is NonNullable<typeof item> => !!item);
}

export function mapClaimTypes(options: any[]): any[] {
  return (options || []).map((opt) => ({
    id: String(opt.id),
    name: opt.name,
    description: opt.description || '',
    isActive: opt.isActive !== false,
  }));
}

export function mapClaimTags(options: any[]): any[] {
  return (options || []).map((opt) => ({
    id: String(opt.id),
    name: opt.name,
    claimTypeId: String(opt.parentId ?? opt.claimTypeId ?? ''),
    isActive: opt.isActive !== false,
  }));
}

export function mapNutrientGroups(options: any[]): any[] {
  return (options || [])
    .map((group: any) => ({
      ...group,
      id: String(group.id ?? group.nutrientGroupId ?? group.groupId ?? ''),
      name: group.name,
      isActive: group.isActive !== false,
    }))
    .filter((group: any) => !!group.id);
}

export function mapNutrientTags(options: any[]): any[] {
  return (options || [])
    .map((tag: any) => ({
      ...tag,
      id: String(tag.id ?? tag.tagId ?? tag.nutrientTagId ?? ''),
      name: tag.name,
      nutrientGroupId: String(tag.parentId ?? tag.nutrientGroupId ?? tag.groupId ?? ''),
      groupId: String(tag.parentId ?? tag.nutrientGroupId ?? tag.groupId ?? ''),
      isActive: tag.isActive !== false,
    }))
    .filter((tag: any) => !!tag.id);
}

/** Unit Dosage Master — supplement nutrient units (PostgreSQL UnitDosageMasters). */
export function mapUnitDosageMastersFromMetadata(metadata: any): any[] {
  const list = getMetadataSectionList(metadata, ['unitDosageMasters', 'UnitDosageMasters']);
  return normalizeSelectableOptions(
    (list || []).map((item: any) => ({
      id: item.id ?? item.Id,
      name: item.name ?? item.Name ?? item.unitName ?? item.UnitName,
      isActive: item.isActive !== false,
    }))
  );
}

export function mapFssaiNutrientsFromMetadata(items: any[]): Array<{
  id: string;
  name: string;
  nutrientType?: string;
  itemUomMasterId?: string | null;
  uomName?: string | null;
}> {
  return (items || [])
    .map((item: any) => ({
      id: String(item.id ?? item.Id ?? ''),
      name: item.name ?? item.Name ?? '',
      nutrientType: item.code ?? item.Code ?? item.nutrientType ?? item.NutrientType,
      itemUomMasterId: item.parentId ?? item.ParentId ?? null,
      uomName: item.label ?? item.Label ?? item.uomName ?? null,
    }))
    .filter((item) => !!item.id && !!item.name);
}

export function buildNutrientTagGroups(
  groups: any[],
  tags: any[]
): Array<{ group: string; groupName: string; tags: Array<{ label: string; colorClass: string }> }> {
  return (groups || []).map((group) => ({
    group: group.name,
    groupName: group.name,
    tags: (tags || [])
      .filter((tag) => String(tag.nutrientGroupId ?? tag.groupId) === String(group.id))
      .map((tag) => ({ label: tag.name, colorClass: 'badge-light-primary' })),
  }));
}

export function refreshShelfLifeUnitOptions(units: any[]): Array<{ id: string; name: string }> {
  const timeUnitPattern = /\b(day|days|month|months|year|years|week|weeks|hour|hours)\b/i;
  const fromItemUom = (units || [])
    .filter((u: any) => timeUnitPattern.test(String(u.name ?? u.measurement ?? u.code ?? '')))
    .map((u: any) => ({
      id: String(u.id),
      name: u.name ?? u.measurement ?? u.code,
    }));

  if (fromItemUom.length) {
    return fromItemUom;
  }

  return [
    { id: '1', name: 'Day' },
    { id: '2', name: 'Month' },
    { id: '3', name: 'Year' },
  ];
}

export async function loadMasterList(
  masterService: MasterService,
  listFn: (req: PaginationRequest) => Promise<any>,
  pageSize = 1000
): Promise<any[]> {
  const paginationRequest = new PaginationRequest();
  paginationRequest.pageNo = 1;
  paginationRequest.pageSize = pageSize;
  paginationRequest.searchString = '';
  const response = await listFn(paginationRequest);
  return extractMasterPage(response).items.filter((item: any) => item?.isActive !== false);
}
